/**
 * useUpdateServiceAccount Hook
 *
 * React Query mutation hook for updating a service account.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { serviceAccountService } from '../../../api/service-account.service';
import { SERVICE_ACCOUNTS_QUERY_KEY } from './useServiceAccounts';
import { SERVICE_ACCOUNT_QUERY_KEY } from './useServiceAccount';
import { getErrorMessage } from '../../../lib/utils/errors';
import type { ServiceAccountUpdateDTO, ServiceAccountSanitized } from '../../../types/service-account.types';

interface UpdateServiceAccountParams {
  serviceAccountId: string;
  data: ServiceAccountUpdateDTO;
}

interface UseUpdateServiceAccountOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for updating a service account
 */
export const useUpdateServiceAccount = (
  tenantId: string,
  options?: UseUpdateServiceAccountOptions
): UseMutationResult<ServiceAccountSanitized, Error, UpdateServiceAccountParams> => {
  const queryClient = useQueryClient();

  return useMutation<ServiceAccountSanitized, Error, UpdateServiceAccountParams>({
    mutationFn: ({ serviceAccountId, data }) => serviceAccountService.updateServiceAccount(tenantId, serviceAccountId, data),
    onSuccess: (_, { serviceAccountId }) => {
      // Invalidate service accounts list to refresh the data
      queryClient.invalidateQueries({ queryKey: [SERVICE_ACCOUNTS_QUERY_KEY, tenantId] });
      // Invalidate specific service account
      queryClient.invalidateQueries({ queryKey: [SERVICE_ACCOUNT_QUERY_KEY, tenantId, serviceAccountId] });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
};
